/**
 * Centralized route configuration for the application.
 * This file defines all routes and their access requirements in one place.
 *
 * Access levels:
 * - STD: Student
 * - INST: Instructor
 * - DDC: Dean/Department Chair
 * - COMMENT_AUDITING: Able to audit report comments
 * - WEB: Web user (department admin)
 * - TEMP: Temporary access
 * - OFFICE: Office staff
 * - ADMIN: Administrator (has access to everything)
 */

// Define all routes with their access requirements and navbar grouping
export const ROUTE_CONFIG = {
	// ===== REPORTING ROUTES =====
	"/reporting/My-SPOT-Reports": {
		access: ["INST", "ADMIN"],
		navGroup: "reporting",
		label: "My SPOT Reports",
	},
	"/reporting/My-USRI-Reports": {
		access: ["INST", "ADMIN"],
		navGroup: "reporting",
		label: "My USRI Reports",
	},
	"/reporting/search-spot-reports": {
		access: ["STD", "ADMIN"],
		navGroup: "reporting",
		label: "Search SPOT Reports",
	},
	"/reporting/Search-SPOT-Results": {
		access: ["STD", "ADMIN"],
		navGroup: null, // Not shown in navbar
	},
	"/reporting/search-usri-reports": {
		access: ["STD", "ADMIN"],
		navGroup: "reporting",
		label: "Search USRI Reports",
	},
	"/reporting/DDC-Designee-Form": {
		access: ["DDC"],
		navGroup: "reporting",
		label: "DDC Desginee Form",
	},
	"/reporting/Redact-Report-Comments": {
		access: ["COMMENT_AUDITING","ADMIN"],
		navGroup: "reporting",
		label: "Redact Report Comments",
	},
	"/reporting/SPOT-DDC-Search": {
		access: ["DDC","DDC_DESIGNEE"],
		navGroup: "reporting",
		label: "Dean and Chair SPOT Reports",
	},
	"/reporting/SPOT-DDC-Reports": {
		access: ["DDC","DDC_DESIGNEE"],
		navGroup: null,
	},
	"/reporting/USRI-DDC-Search": {
		access: ["DDC","DDC_DESIGNEE"],
		navGroup: "reporting",
		label: "Dean and Chair USRI Reports",
	},
	"/reporting/USRI-DDC-Search-Results": {
		access: ["DDC","DDC_DESIGNEE"],
		navGroup: null,
	},
	"/reporting/Report-View": {
		access: ["STD", "INST", "DDC", "ADMIN","DDC_DESIGNEE"],
		navGroup: null,
	},
	"/reporting": {
		access: ["STD", "INST", "DDC", "ADMIN","DDC_DESIGNEE"],
		navGroup: null,
	},

	// ===== SPOT ROUTES =====
	"/spot/spot-ordering": {
		access: ["WEB", "ADMIN"],
		navGroup: "spot",
		label: "SPOT Ordering",
	},
	"/spot/spot-dept-summary": {
		access: ["WEB", "ADMIN"],
		navGroup: "spot",
		label: "SPOT Summary",
	},
	"/spot/spot-report-distribution": {
		access: ["WEB", "ADMIN"],
		navGroup: "spot",
		label: "Report Distribution",
	},
	"/spot/view-blocks": {
		access: ["WEB", "ADMIN"],
		navGroup: "spot",
		label: "View Blocks",
	},
	"/spot/view-catalog": {
		access: ["WEB", "ADMIN"],
		navGroup: "spot",
		label: "View Question Catalog",
	},
	"/spot/AddRequisition": {
		access: ["WEB", "ADMIN"],
		navGroup: null,
	},
	"/spot/ManageRequisitions": {
		access: ["WEB", "ADMIN"],
		navGroup: null,
	},
	"/spot/AutoRequisition": {
		access: ["WEB", "ADMIN"],
		navGroup: null,
	},
	"/spot": {
		access: ["WEB", "ADMIN"],
		navGroup: null,
	},

	// ===== TEST SCORING / EXAM ROUTES =====
	"/test-scoring/request-for-service": {
		access: ["INST", "WEB", "TEMP", "OFFICE", "ADMIN"],
		navGroup: "exam",
		label: "Request Scanning",
	},
	"/test-scoring/view-scanning-requests": {
		access: ["INST", "WEB", "TEMP", "ADMIN"],
		navGroup: "exam",
		label: "View Requests",
	},

	"/test-scoring/exam-grading": {
		access: ["ADMIN"],
		navGroup: "exam",
		label: "Exam Grading",
	},
	"/test-scoring/success": {
		access: ["INST", "WEB", "TEMP", "ADMIN"],
		navGroup: null,
	},

	"/test-scoring/confirm-exam-dates": {
		access: ["INST", "ADMIN"],
		navGroup: null,
	},

	"/test-scoring": {
		access: ["INST", "WEB", "TEMP", "ADMIN"],
		navGroup: null,
	},

	// ===== UTILITIES ROUTES =====
	"/utilities/AddBlocks": {
		access: ["ADMIN"],
		navGroup: "utilities",
		label: "Add Blocks",
	},
	"/utilities/AddItem": {
		access: ["ADMIN"],
		navGroup: "utilities",
		label: "Add Item",
	},
	"/utilities/AddUser": {
		access: ["ADMIN"],
		navGroup: "utilities",
		label: "Add User",
	},
	"/utilities/EditClasses": {
		access: ["WEB", "ADMIN"],
		navGroup: "utilities",
		label: "Edit Classes",
	},

	"/utilities/ViewDDCs": {
		access: ["ADMIN","PROVOST"],
		navGroup: "utilities",
		label: "View DDC's",
	},

	// ===== ADMIN ROUTES =====
	"/admin/Courses-Without-SPOTs": {
		access: ["ADMIN"],
		navGroup: "admin",
		label: "Courses Without SPOTs",
	},
	"/admin/TrackRequisitions": {
		access: ["ADMIN"],
		navGroup: "admin",
		label: "Track Requisitions",
	},
	"/admin/AdminUtils": {
		access: ["ADMIN"],
		navGroup: "admin",
		label: "Admin Utils",
	},
	"/admin/Chair-Report-Generation": {
		access: ["ADMIN"],
		navGroup: "admin",
		label: "Chair Report Generation",
	},
	// "/admin/Edit-Report-Comments": {
	// 	access: ["ADMIN"],
	// 	navGroup: "admin",
	// 	label: "Edit Report Comments",
	// },
		"/admin/Update-DDCs": {
		access: ["ADMIN"],
		navGroup: "admin",
		label: "Update DDC's",
	},
	"/admin/MidtermView": {
		access: ["ADMIN"],
		navGroup: "admin",
		label: "Midterm View",
	},
	"/admin/SPOT-Faculty-Department-Search": {
		access: ["ADMIN"],
		navGroup: null, // Currently commented out in navbar
	},

	// ===== GLOBAL ROUTES (accessible to all authenticated users) =====
	"/": {
		access: ["*"], // Special marker for "all authenticated users"
		navGroup: null,
	},
	"/FAQ": {
		access: ["*"],
		navGroup: null,
	},
	"/UserSettings": {
		access: ["*"],
		navGroup: null,
	},
};

// External links for navbar (not routes, but shown in menus)
export const EXTERNAL_LINKS = {
	spot: [
		{
			label: "SPOT Tutorial",
			url: "https://www.youtube.com/watch?v=CmXNt17Tj-8&ab_channel=GeorgeNassar",
			access: ["WEB", "ADMIN"],
		},
	],
};

// Navigation group configuration (order matters for display)
export const NAV_GROUPS = {
	reporting: {
		label: "SPOT & USRI Reports",
		access: ["STD", "INST", "DDC", "ADMIN","COMMENT_AUDITING","DDC_DESIGNEE"], // Who can see this menu
	},

	exam: {
		label: "Test Scoring",
		access: ["INST", "WEB", "TEMP", "OFFICE", "ADMIN"],
	},
	spot: {
		label: "SPOT Surveys",
		access: ["WEB", "ADMIN"],
	},
	utilities: {
		label: "Utilities",
		access: ["WEB", "ADMIN","PROVOST"],
	},
	admin: {
		label: "Admin",
		access: ["ADMIN"],
	},
};

// Legacy routes we still honor; we redirect and authorize using the new paths
export const LEGACY_ROUTE_ALIASES = {
	// "/My-SPOT-Reports": "/reporting/My-SPOT-Reports",
	// "/My-SPOT-reports": "/reporting/My-SPOT-Reports",
	// "/reporting/my-spot-reports": "/reporting/My-SPOT-Reports",
	// "/My-USRI-Reports": "/reporting/My-USRI-Reports",
	// "/My-USRI-reports": "/reporting/My-USRI-Reports",
	// "/reporting/my-usri-reports": "/reporting/My-USRI-Reports",
	"/SearchSpotReports": "/reporting/search-spot-reports",
	"/SearchSpotResults": "/reporting/Search-SPOT-Results",
	"/Search-SPOT-Results": "/reporting/Search-SPOT-Results",
	"/SearchUsriResults": "/reporting/search-usri-reports",
	"/USRI-Student-Search-Results": "/reporting/USRI-Student-Search-Results",
	"/SPOT-DDC-Search": "/reporting/SPOT-DDC-Search",
	"/SPOT-DDC-Reports": "/reporting/SPOT-DDC-Reports",
	"/USRI-DDC-Search": "/reporting/USRI-DDC-Search",
	"/USRI-DDC-Search-Results": "/reporting/USRI-DDC-Search-Results",
	"/Report-View": "/reporting/Report-View",
	"/spot-ordering": "/spot/spot-ordering",
	"/spot-dept-summary": "/spot/spot-dept-summary",
	"/spot-report-distribution": "/spot/spot-report-distribution",
	"/view-blocks": "/spot/view-blocks",
	"/view-catalog": "/spot/view-catalog",
	"/AddRequisition": "/spot/AddRequisition",
	"/ManageRequisitions": "/spot/ManageRequisitions",
	"/AutoRequisition": "/spot/AutoRequisition",
	"/request-for-service": "/test-scoring/request-for-service",
	"/view-scanning-requests": "/test-scoring/view-scanning-requests",
	"/exam-grading": "/test-scoring/exam-grading",
	"/success": "/test-scoring/success",
	"/confirm-exam-dates": "/test-scoring/confirm-exam-dates",
	"/exams": "/test-scoring",
	"/AddBlocks": "/utilities/AddBlocks",
	"/AddItem": "/utilities/AddItem",
	"/AddUser": "/utilities/AddUser",
	"/EditClasses": "/utilities/EditClasses",
	"/SPOTnotSetup": "/admin/SPOTnotSetup",
	"/TrackRequisitions": "/admin/TrackRequisitions",
	"/AdminUtils": "/admin/AdminUtils",
	"/Chair-Report-Generation": "/admin/Chair-Report-Generation",
	"/MidtermView": "/admin/MidtermView",
	"/SPOT-Faculty-Department-Search": "/admin/SPOT-Faculty-Department-Search",
};

/**
 * Check if a user with given access levels can access a route
 * @param {string} pathname - The route path to check
 * @param {string[]} userAccess - Array of user's access levels (e.g., ["INST", "DDC"])
 * @returns {boolean}
 */
export function canAccessRoute(pathname, userAccess) {
	if (!userAccess || userAccess.length === 0) return false;

	const normalizedPath = LEGACY_ROUTE_ALIASES[pathname] || pathname;

	// Admin can access everything
	if (userAccess.includes("ADMIN")) return true;

	const routeConfig = ROUTE_CONFIG[normalizedPath];

	// If route not defined, deny access
	if (!routeConfig) return false;

	// Global routes accessible to all authenticated users
	if (routeConfig.access.includes("*")) return true;

	// Check if any of user's access levels match route's requirements
	return routeConfig.access.some((level) => userAccess.includes(level));
}

/**
 * Get all routes for a specific nav group that a user can access
 * @param {string} groupName - The nav group name (e.g., "reporting", "spot")
 * @param {string[]} userAccess - Array of user's access levels
 * @returns {Array<{path: string, label: string}>}
 */
export function getNavGroupRoutes(groupName, userAccess) {
	if (!userAccess || userAccess.length === 0) return [];

	const isAdmin = userAccess.includes("ADMIN");

	return Object.entries(ROUTE_CONFIG)
		.filter(([path, config]) => {
			if (config.navGroup !== groupName) return false;
			if (!config.label) return false; // Must have a label to show in nav

			if (isAdmin) return true;
			return config.access.some((level) => userAccess.includes(level));
		})
		.map(([path, config]) => ({
			path,
			label: config.label,
		}));
}

/**
 * Check if a user can see a specific nav group
 * @param {string} groupName - The nav group name
 * @param {string[]} userAccess - Array of user's access levels
 * @returns {boolean}
 */
export function canSeeNavGroup(groupName, userAccess) {
	if (!userAccess || userAccess.length === 0) return false;

	const group = NAV_GROUPS[groupName];
	if (!group) return false;

	if (userAccess.includes("ADMIN")) return true;

	if (groupName === 'reporting' && sessionStorage.commentEditing === 'true') return true;

	return group.access.some((level) => userAccess.includes(level));
}

/**
 * Get external links for a nav group that a user can access
 * @param {string} groupName - The nav group name
 * @param {string[]} userAccess - Array of user's access levels
 * @returns {Array<{label: string, url: string}>}
 */
export function getNavGroupExternalLinks(groupName, userAccess) {
	if (!userAccess || userAccess.length === 0) return [];

	const links = EXTERNAL_LINKS[groupName] || [];
	const isAdmin = userAccess.includes("ADMIN");

	return links.filter((link) => {
		if (isAdmin) return true;
		return link.access.some((level) => userAccess.includes(level));
	});
}
